/**
 * Batch Genealogy — Lineage Graph (React Flow DAG).
 *
 * Vertical lineage: final product on top, ancestry flowing DOWN into stage
 * bands (Step E → A), parent→product edges, status-colour-coded batch cards.
 * Each stage row is CENTRED on a common axis, so a parent card sits centred
 * above its children (a symmetric pyramid). Clicking a card raises
 * onSelectBatch(id) so the parent can open the batch detail side panel.
 *
 * The tree is shown at full size (not shrunk to fit a fixed box): the canvas
 * height grows with the number of stages and the page scroll does the rest.
 *
 * The stage labels ("STAGE D" + "Intermediate" / "Final Product") are NOT
 * canvas nodes — they live in a fixed rail pinned to the LEFT edge. Each label
 * tracks its row's live screen position from the React Flow viewport, so it
 * pans/zooms vertically in sync with its row while staying locked to the left
 * corner horizontally. The stage's product description is shown in the batch
 * detail drawer, not here.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Controls, Handle, Position, MarkerType, ReactFlowProvider, useReactFlow, useViewport,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { STATUS, stageRole } from '../statusConfig';

const CARD_W = 280;               // full card width (border-box, padding included)
const CARD_H = 160;               // approx. rendered card height — used to centre a stage label on its row
const ROW_H = 220;
const COL_W = 310;                // CARD_W + 30px gap between sibling cards

// Left rail holding the stage labels. The React Flow canvas starts just to its
// right so cards can never slide under a label.
const LABEL_W = 220;

// The graph is drawn at MAX_FIT_ZOOM when it fits, and never smaller than
// MIN_FIT_ZOOM: a stage with more batches than fit across the screen makes the
// canvas scroll SIDEWAYS instead of shrinking every card. The canvas grows to the
// lineage's full height, so the PAGE scroll reveals the lower stages — the cards
// stay readable without the +/- controls.
const MAX_FIT_ZOOM = 1.2;
const MIN_FIT_ZOOM = 1;
const PAD = 32;                   // canvas padding around the tree (screen px)
const SCROLLBAR_H = 16;           // room for the horizontal scrollbar when a stage overflows

// The status colour is applied ONLY to the card's top border and the status
// dot; every piece of text inside the card stays neutral.
const BatchNode = ({ data }) => {
  const n = data.node;
  const s = STATUS[n.status] || STATUS.released;
  const consumedLine =
    n.parents && n.parents.length ? n.parents.join(' + ') : (n.keyMaterial || '— raw materials');
  const label = { fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#5b6675', textTransform: 'uppercase' };
  const value = { fontSize: 16, fontWeight: 400, color: '#33404f', fontVariantNumeric: 'tabular-nums' };
  return (
    <div
      style={{
        width: CARD_W, boxSizing: 'border-box', background: '#fff', border: '1px solid #e3e7ed',
        borderTop: `3px solid ${s.color}`, borderRadius: 10,
        padding: '14px 16px', boxShadow: '0 2px 6px rgba(15,23,42,0.08)', cursor: 'pointer',
      }}
    >
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
        <span style={{ fontSize: 17, fontWeight: 700, color: '#1b2433' }}>{n.id}</span>
        {n.dev && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#33404f', background: '#fff', border: '1px solid #e3e7ed', borderRadius: 10, padding: '1px 8px' }}>⚑ DEV</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
        <div>
          <div style={label}>Yield</div>
          <div style={value}>{n.yield}%</div>
        </div>
        <div>
          <div style={label}>Qty</div>
          <div style={value}>{n.qty}</div>
        </div>
      </div>
      <div
        style={{
          fontSize: 14, color: '#33404f', borderTop: '1px solid #eef1f5', marginTop: 10, paddingTop: 10,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {consumedLine}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
        <span style={{ color: '#5b6675' }}>{`${n.materials} materials`}</span>
        <span style={{ color: '#1f5fd1', textDecoration: 'underline' }}>View More</span>
      </div>
    </div>
  );
};

const nodeTypes = { batch: BatchNode };

// Descending stage comparator (final on top) — mirrors the middleware's
// compareStageDesc so a step order can be synthesised on the client when needed.
const compareStageDesc = (a, b) => String(b || '').localeCompare(String(a || ''));

/**
 * Fixed left rail. Reads the live viewport (pan y + zoom) and re-positions each
 * label at its row's current screen-Y (vertically centred on the row's cards) so
 * the label stays glued to its row while pinned to the left corner. Text is a
 * fixed readable size, not scaled with the zoom. pointerEvents:none so panning
 * works straight through the rail.
 */
const StageLabelRail = ({ rows }) => {
  const { y: vy, zoom } = useViewport();
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, width: LABEL_W, height: '100%',
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
      }}
    >
      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            position: 'absolute', right: 0, top: r.y * zoom + vy, transform: 'translateY(-50%)',
            width: LABEL_W, textAlign: 'right', paddingRight: 24, boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: '#4b2e83', textTransform: 'uppercase', lineHeight: 1.3 }}>
            {`Stage ${r.id}`}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#33404f', lineHeight: 1.3 }}>{r.role}</div>
        </div>
      ))}
    </div>
  );
};

const LineageGraphInner = ({ steps, nodes, onSelectBatch }) => {
  const { setViewport } = useReactFlow();
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const [canvasW, setCanvasW] = useState(0);

  // Track the canvas width (container minus the label rail) so the fit zoom and
  // the horizontal centring follow window / sidebar resizes.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setCanvasW(Math.max(0, el.clientWidth - LABEL_W));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { rfNodes, rfEdges, rows, maxPerRow } = useMemo(() => {
    // Group the batch nodes by their stage code.
    const byStep = {};
    nodes.forEach((n) => { (byStep[n.step] = byStep[n.step] || []).push(n); });

    // Row order = the stages that ACTUALLY have nodes, in the order given by the
    // API `steps` list, with any extra node-stages appended (sorted final→first).
    // Deriving the order from the nodes themselves — rather than trusting the
    // steps list to match — guarantees every batch card is placed and rendered
    // even if steps[] and node.step ever drift apart.
    const stepMeta = new Map();
    (steps || []).forEach((s) => stepMeta.set(s.id, s));
    const fromSteps = (steps || []).map((s) => s.id).filter((id) => byStep[id]);
    const extra = Object.keys(byStep)
      .filter((id) => id && !fromSteps.includes(id))
      .sort(compareStageDesc);
    const order = [...fromSteps, ...extra];

    const rowOf = {};
    order.forEach((id, i) => { rowOf[id] = i; });

    // Centre each stage's cards on the common axis x = 0, so a single-batch
    // stage sits dead-centre and multi-batch stages spread symmetrically around
    // it — a parent card ends up centred above its children (the pyramid in the
    // design). The viewport below then centres the whole tree in the canvas.
    const cardNodes = [];
    order.forEach((stepId) => {
      const list = byStep[stepId] || [];
      const c = list.length;
      list.forEach((n, i) => {
        cardNodes.push({
          id: n.id,
          type: 'batch',
          position: { x: (i - (c - 1) / 2) * COL_W, y: rowOf[stepId] * ROW_H },
          data: { node: n },
          draggable: true,
        });
      });
    });

    // Row descriptors for the pinned left rail (graph-space Y of the row's
    // vertical centre + the stage role).
    const rowList = order.map((id) => ({
      id, y: rowOf[id] * ROW_H + CARD_H / 2, role: stageRole(stepMeta.get(id)),
    }));

    const edges = [];
    nodes.forEach((n) => {
      (n.parents || []).forEach((pid) => {
        if (!nodes.find((x) => x.id === pid)) return;
        edges.push({
          id: `${pid}->${n.id}`,
          source: pid,
          target: n.id,
          // straight diagonal connectors — matches the design deck (not right-angle routing)
          type: 'straight',
          markerEnd: { type: MarkerType.ArrowClosed, color: '#b6becb', width: 16, height: 16 },
          style: { stroke: '#b6becb', strokeWidth: 1.5 },
        });
      });
    });

    const widest = order.reduce((m, id) => Math.max(m, (byStep[id] || []).length), 1);
    return { rfNodes: cardNodes, rfEdges: edges, rows: rowList, maxPerRow: widest };
  }, [nodes, steps]);

  // Natural (zoom 1) size of the tree → fit zoom (width-limited, clamped) →
  // pane width (wider than the visible canvas when the widest stage overflows,
  // which makes it scroll sideways) and the height that holds every stage row.
  const contentW = (maxPerRow - 1) * COL_W + CARD_W;
  const contentH = (rows.length - 1) * ROW_H + CARD_H;
  const zoom = canvasW
    ? Math.min(MAX_FIT_ZOOM, Math.max(MIN_FIT_ZOOM, (canvasW - 2 * PAD) / contentW))
    : MAX_FIT_ZOOM;
  const paneW = Math.max(canvasW, Math.ceil(contentW * zoom + 2 * PAD));
  const overflowsX = paneW > canvasW;
  const graphH = Math.ceil(contentH * zoom + 2 * PAD) + (overflowsX ? SCROLLBAR_H : 0);

  // Every stage row is centred on graph-space x = CARD_W / 2 (see layout above),
  // so that point goes to the middle of the pane; the top row sits PAD down.
  // A passive effect (not layout) so React Flow's own mount effects — which set up
  // the zoom behaviour setViewport drives — have already run.
  useEffect(() => {
    if (!canvasW) return;
    setViewport({ x: paneW / 2 - (CARD_W / 2) * zoom, y: PAD, zoom });
  }, [canvasW, paneW, zoom, rfNodes, setViewport]);

  // When the pane is wider than the canvas, open it scrolled to the middle so the
  // final product (top, centred) is in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [canvasW, paneW]);

  const handleNodeClick = (_evt, node) => {
    if (node.type === 'batch' && onSelectBatch) onSelectBatch(node.id);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', height: graphH, minHeight: 340, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
      {/* Canvas starts just right of the pinned label rail so cards never sit under a
          label, and scrolls sideways when the widest stage is wider than the screen. */}
      <div ref={scrollRef} style={{ position: 'absolute', top: 0, left: LABEL_W, right: 0, bottom: 0, overflowX: 'auto', overflowY: 'hidden' }}>
        <div style={{ width: paneW || '100%', height: '100%' }}>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            minZoom={0.3}
            maxZoom={1.5}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
            // Don't let the graph hijack the mouse wheel. By default React Flow
            // preventDefaults wheel events to zoom/pan, which turns this canvas
            // into a second scroll surface that fights the page scroll. With these
            // off, the wheel scrolls the PAGE straight through the graph (and a
            // sideways trackpad / shift+wheel scrolls a wide graph horizontally).
            zoomOnScroll={false}
            panOnScroll={false}
            preventScrolling={false}
            zoomOnDoubleClick={false}
          />
        </div>
      </div>

      <StageLabelRail rows={rows} />
      {/* Zoom controls live in the fixed rail's top-left corner (not inside the
          scrolling pane) so they stay in view and never cover a card. */}
      <Controls showInteractive={false} position="top-left" />
    </div>
  );
};

const LineageGraph = (props) => (
  <ReactFlowProvider>
    <LineageGraphInner {...props} />
  </ReactFlowProvider>
);

export default LineageGraph;
